import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useContext } from "react";
import { DashboardContext } from "../../../../DashboardContext/DashboardContextProvider";

// Gets all authorization letters for a client
const get_auth_letters = async (clientId) => {
  try {
    const response = await axios.get(`/api/get-auth-letters/${clientId}/`, {
      withCredentials: true,
    });
    console.log("Authorization letters: ", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching authorization letters: ", error);
    throw error;
  }
};

export const useAuthorizationLetters = (clientId) => {
  return useQuery({
    queryKey: ["authorizationLetters", clientId],
    queryFn: () => get_auth_letters(clientId),
    enabled: !!clientId,
  });
};

// Adds existing authorization letter to the project
const add_letter = async (data) => {
  const { projectId, letterId } = data;
  try {
    const response = await axios.patch(
      `/api/add-letter/${projectId}/${letterId}/`,
      {},
      {
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error adding authorization letter: ", error);
    throw error;
  }
};

export const useAddLetter = (clientId, setState) => {
  const { queryClient } = useContext(DashboardContext);
  return useMutation({
    mutationFn: add_letter,
    onSuccess: (data) => {
      console.log("Authorization letter added successfully: ", data);
      setState((state) => ({
        ...state,
        authorizationLetter: {
          id: data.id,
          signer: data.signer,
          isSigned: data.is_signed,
          letter: data.letter,
          companyName: data.companyName,
          title: data.title,
        },
      }));
    },
    onError: (error) => {
      console.error("Error adding authorization letter: ", error);
    },
    onSettled: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: ["authorizationLetters", clientId],
      });
    },
  });
};

// Adds existing authorization letter to the project
const remove_letter = async (data) => {
  const { projectId } = data;
  try {
    const response = await axios.patch(
      `/api/remove-letter/${projectId}/`,
      {},
      {
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error removing authorization letter: ", error);
    throw error;
  }
};

export const useRemoveLetter = (clientId, setState) => {
  const { queryClient } = useContext(DashboardContext);
  return useMutation({
    mutationFn: remove_letter,
    onSuccess: (data) => {
      console.log("Authorization letter removed successfully: ", data);
      setState((state) => ({
        ...state,
        authorizationLetter: {
          id: "",
          signer: "",
          isSigned: false,
          letter: "",
          companyName: "",
          title: "",
        },
      }));
    },
    onError: (error) => {
      console.error("Error removing authorization letter: ", error);
    },
    onSettled: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: ["authorizationLetters", clientId],
      });
    },
  });
};

const generate_authorization_letter = async (data) => {
  const { clientId, projectId, signer, company, title } = data;
  try {
    const response = await axios.patch(
      `/api/generate-auth-letter/${clientId}/${projectId}/`,
      {
        signer,
        companyName: company,
        isSigned: false,
        title,
      },
      {
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error generating authorization letter: ", error);
    throw error;
  }
};

export const useGenerateLetter = (setState) => {
  const { queryClient, clientId } = useContext(DashboardContext);
  return useMutation({
    mutationFn: generate_authorization_letter,
    onSuccess: (data) => {
      console.log("Authorization letter generated successfully: ", data);
      setState((state) => ({
        ...state,
        authorizationLetter: {
          id: data.id,
          signer: data.signer,
          isSigned: data.is_signed,
          letter: data.letter,
          companyName: data.companyName,
          title: data.title,
        },
      }));
    },
    onError: (error) => {
      console.error("Error generating authorization letter: ", error);
    },
    onSettled: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: ["authorizationLetters", clientId],
      });
    },
  });
};

const upload_signed_letter = async (data) => {
  const { letterId, letter } = data;
  try {
    const formData = new FormData();
    formData.append("letter", letter);

    const response = await axios.patch(
      `/api/upload-signed-letter/${letterId}/`,
      formData,
      {
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error uploading signed letter: ", error);
    throw error;
  }
};

export const useUploadSignedLetter = (setState, clientId, projectId) => {
  const { queryClient } = useContext(DashboardContext);
  return useMutation({
    mutationFn: upload_signed_letter,
    onSuccess: (data) => {
      console.log("Signed letter uploaded successfully: ", data);
      setState((state) => ({
        ...state,
        authorizationLetter: {
          id: data.id,
          signer: data.signer,
          isSigned: data.is_signed,
          letter: data.letter,
          companyName: data.companyName,
          title: data.title,
        },
      }));
    },
    onError: (error) => {
      console.error("Error uploading signed letter: ", error);
    },
    onSettled: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: ["authorizationLetters", clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ["currentProject", clientId, projectId],
      });
    },
  });
};

const delete_authorization_letter = async (data) => {
  const { projectId, letterId } = data;
  try {
    const response = await axios.delete(
      `/api/delete-auth-letter/${projectId}/${letterId}/`,
      {
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting authorization letter: ", error);
    throw error;
  }
};

export const useDeleteLetter = (setState) => {
  const { queryClient, clientId } = useContext(DashboardContext);
  return useMutation({
    mutationFn: delete_authorization_letter,
    onSuccess: (data) => {
      console.log("Authorization letter deleted successfully: ", data);
      setState((state) => ({
        ...state,
        authorizationLetter: {
          id: "",
          signer: "",
          isSigned: false,
          letter: "",
          companyName: "",
          title: "",
        },
      }));
    },
    onError: (error) => {
      console.error("Error deleting authorization letter: ", error);
    },
    onSettled: (variables) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: ["authorizationLetters", clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ["currentProject", clientId, variables.projectId],
      });
    },
  });
};
